import type {
  DateRange,
  FuelTransactionBodyContent,
  PagedTransactionStatsByVehicle,
  PagedTransactions,
  TransactionStatsByVehicleSummary,
  TransactionSummaryBodyContent,
  TransactionSummaryBodyContentByPeriodType,
  TransactionsAvgFuelConsumptionAndCostPerKm,
  TransactionsHistoryBodyContent,
  TransactionsSummary,
  TransactionsSumTotalCostByFuelType,
} from '@cobliteam/common-types';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import type { Configuration, FuelTransaction } from '../..';
import type { FuelTransactionResponse } from '../../__generated__/graphql';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { IRequest } from '../../request/Types';
import { toSaveFuelTransactionBody } from './APITransformers';
import type { UpdateTransactionRequest } from './Definitions/APITypes';

export class NewFuelCardRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getTransactionsHistory = (body: TransactionsHistoryBodyContent): Promise<PagedTransactions> => {
    const requestData: IRequest = {
      endpoint: '/transactions/list',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as PagedTransactions;
    });
  };

  public getTransactionsSummary = (body: TransactionSummaryBodyContent): Promise<TransactionsSummary> => {
    const requestData: IRequest = {
      endpoint: '/transactions/summary',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as TransactionsSummary;
    });
  };

  public getTransactionsAvgFuelConsumptionAndCostPerKm = (body: TransactionSummaryBodyContentByPeriodType): Promise<TransactionsAvgFuelConsumptionAndCostPerKm[]> => {
    const requestData: IRequest = {
      endpoint: '/transactions/avg-fuel-consumption-and-cost-per-km',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as TransactionsAvgFuelConsumptionAndCostPerKm[];
    });
  };

  public getTransactionsSumTotalCostByFuelType = (body: TransactionSummaryBodyContentByPeriodType): Promise<TransactionsSumTotalCostByFuelType[]> => {
    const requestData: IRequest = {
      endpoint: '/transactions/sum-total-cost-by-fuel-type',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as TransactionsSumTotalCostByFuelType[];
    });
  };

  public getTransactionStatsByVehicleSummary = (body: TransactionSummaryBodyContent): Promise<TransactionStatsByVehicleSummary> => {
    const requestData: IRequest = {
      endpoint: '/transactions/stats-by-vehicle/summary',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as TransactionStatsByVehicleSummary;
    });
  };

  public getTransactionStatsByVehicle = (body: TransactionsHistoryBodyContent): Promise<PagedTransactionStatsByVehicle> => {
    const requestData: IRequest = {
      endpoint: '/transactions/stats-by-vehicle',
      method: HttpMethod.POST,
      body: JSON.stringify({
        ...snakecaseKeys(body),
      }),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as PagedTransactionStatsByVehicle;
    });
  };

  public getFuelTransaction = async (transactionId: string): Promise<FuelTransaction> => {
    const requestData: IRequest = {
      endpoint: `/transactions/${transactionId}`,
      method: HttpMethod.GET,
    };

    const response = await this.requester.request(requestData);

    return camelcaseKeys(response.data, { deep: true }) as FuelTransaction;
  };

  public getTransactionsReport = (period: DateRange, vehicleGroupIds: string[]): Promise<Blob> => {
    const requestData: IRequest = {
      endpoint: '/transactions/report',
      method: HttpMethod.POST,
      body: JSON.stringify({
        start_date: new Date(period.start),
        end_date: new Date(period.end),
        vehicle_group_ids: vehicleGroupIds,
      }),
      responseType: 'arraybuffer',
    };

    return this.requester.request(requestData).then((response) => new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  };

  public updateFuelTransaction = (transactionId: string, updateRequest: UpdateTransactionRequest): Promise<void> => {
    const requestData: IRequest = {
      endpoint: `/transactions/${transactionId}`,
      method: HttpMethod.PUT,
      body: JSON.stringify(updateRequest),
    };

    return this.requester.request(requestData).then();
  };

  public createFuelTransaction = (createRequest: FuelTransactionBodyContent): Promise<FuelTransactionResponse> => {
    const transactionBody = toSaveFuelTransactionBody(createRequest);
    const requestData: IRequest = {
      endpoint: '/transactions/create',
      method: HttpMethod.POST,
      body: JSON.stringify(transactionBody),
    };

    return this.requester.request(requestData).then((response) => {
      return camelcaseKeys(response.data, { deep: true }) as FuelTransactionResponse;
    });
  };
}

export default NewFuelCardRequester;
